import React from "react";
import { useState, useEffect } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Navbar,
  Container,
  NavDropdown,
  Nav,
  OverlayTrigger,
  Tooltip,
  Button,
} from "react-bootstrap";
import ModuleSidebar from "../Components/ModuleSidebar/ModuleSidebar";
import SearchModal from "../Components/SearchModal/SearchModal";
import { useAuth } from "../Context/AuthContext";
import { useHttpClient } from "../hooks/http-hook";
import { PiTreeStructureFill } from "react-icons/pi";
import { MdContentCopy } from "react-icons/md";
import { useEditMode } from "../Context/EditModeContext";
import { ModuleProvider } from "../Context/ModuleContext";
import ProgressBar from "react-bootstrap/ProgressBar";
import "./RootLayout.css";
import LessonLayout from "./LessonLayout";
import LanguageSelector from "../Components/LanguageSelector"; // Importer le Language Selector
import { useTranslation } from "react-i18next"; // Importer useTranslation

export default function ModuleLayout() {
  const navigate = useNavigate();
  const { t } = useTranslation(); // Assigner useTranslation à {t}

  const location = useLocation();
  const { id, moduleId } = useParams();
  const { setAuth, authData } = useAuth();
  const { sendRequest } = useHttpClient();
  const [showSearch, setShowSearch] = useState(false);
  const [program, setProgram] = useState(null);
  const { isEditMode, toggleEditMode } = useEditMode();
  const [moduleProgress, setModuleProgress] = useState({ moduleProgress: 0 });

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/programs/${id}`
        );
        setProgram(response.program);
      } catch (err) {
        console.error("Error fetching program:", err);
      }
    };

    const fetchModuleProgress = async () => {
      try {
        const data = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}user-program-progress/module-progress/${moduleId}?programId=${id}&user=${authData.userId}`
        );
        console.log("Module progress:", data);

        setModuleProgress(data);
        console.log(data);
      } catch (err) {
        console.error("Error fetching module progress:", err);
      }
    };

    fetchProgram();
    fetchModuleProgress();
  }, [id, sendRequest]);

  const handleLogout = async () => {
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}auth/logout`,
        "POST",
        null,
        {
          "Content-Type": "application/json",
        }
      );

      console.log("Logout response:", response);

      if (response.message === "Logged out successfully") {
        setAuth(null, null, false);
        console.log("Auth state updated.");
        navigate("/login");
      } else {
        throw new Error("Erreur de déconnexion");
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Vérifier si l'URL actuelle est "module-structure"
  const isModuleStructurePage = location.pathname.includes("module-structure");

  return (
    <ModuleProvider>
      <div className="root-layout d-flex flex-column">
        <header>
          <Navbar
            expand="lg"
            className="bg-white bg-gradient shadow-sm mb-1 py-3"
          >
            <Container fluid>
              <Navbar.Brand
                onClick={() => navigate("/")}
                className="text-primary fw-bold fs-5 text-uppercase"
              >
                <i className="bi bi-mortarboard-fill me-1"></i>
                ProfD
              </Navbar.Brand>

              <Navbar.Toggle aria-controls="responsive-navbar-nav" />

              <Navbar.Collapse id="responsive-navbar-nav">
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center w-100">
                  <div className="d-flex flex-column flex-lg-row align-items-center mb-2 mb-lg-0">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate(`/programs/${id}`)}
                      className="me-0 me-lg-3 mb-2 mb-lg-0"
                    >
                      {t("back")}
                    </Button>

                    <ProgressBar
                      now={moduleProgress.progress}
                      label={`${moduleProgress.progress}%`}
                      style={{
                        width: "100%",
                        minWidth: "200px",
                        maxWidth: "300px",
                      }}
                      className="mb-2 mb-lg-0"
                    />
                  </div>

                  <div className="d-none d-lg-block position-lg-absolute start-50 translate-middle-x">
                    {program && authData.userId === program.created_by._id && (
                      <Nav className="d-flex align-items-center">
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id="tooltip-structure">
                              {t("structure")}
                            </Tooltip>
                          }
                        >
                          <Nav.Link
                            className="d-flex align-items-center me-2"
                            onClick={() =>
                              navigate(
                                `/programs/${id}/module/${moduleId}/module-structure`
                              )
                            }
                          >
                            <PiTreeStructureFill className="text-black fs-5" />
                          </Nav.Link>
                        </OverlayTrigger>
                        <Button
                          variant="outline-primary"
                          onClick={toggleEditMode}
                        >
                          {isEditMode ? t("edit_mode_off") : t("edit_mode_on")}
                        </Button>
                      </Nav>
                    )}
                  </div>
                  <LanguageSelector />

                  <div>
                    <NavDropdown
                      title={<i className="bi bi-person-fill text-black"></i>}
                      id="navbarScrollingDropdown"
                      className="ms-0 ms-lg-3 me-0 me-lg-3 text-black bg-gradient rounded fs-4"
                      align="end"
                    >
                      <NavDropdown.Item onClick={() => navigate("/profile")}>
                        {t("profile")}
                      </NavDropdown.Item>
                      <NavDropdown.Item href="#">
                        {t("settings")}
                      </NavDropdown.Item>
                      <NavDropdown.Divider />
                      {authData.isLoggedIn ? (
                        <NavDropdown.Item onClick={handleLogout}>
                          {t("logout")}
                        </NavDropdown.Item>
                      ) : (
                        <NavDropdown.Item onClick={() => navigate("/login")}>
                          {t("login")}
                        </NavDropdown.Item>
                      )}
                    </NavDropdown>
                  </div>
                </div>
              </Navbar.Collapse>
            </Container>
          </Navbar>
        </header>

        <div className="d-flex flex-grow-1">
          {authData.isLoggedIn && <ModuleSidebar />}
          <main className="flex-grow-1 p-1">
            {/* Afficher LessonLayout uniquement si ce n'est pas la page module-structure */}
            {isModuleStructurePage ? (
              <Outlet />
            ) : (
              <LessonLayout>
                <Outlet />
              </LessonLayout>
            )}
          </main>
        </div>

        <SearchModal
          show={showSearch}
          handleClose={() => setShowSearch(false)}
        />
      </div>
    </ModuleProvider>
  );
}
